import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

function formatShortDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  return `${day}/${month}/${year}`;
}

function formatTinyDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");

  return `${day}/${month}`;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const present = payload.find((item) => item.dataKey === "Présents")?.value || 0;
  const absent = payload.find((item) => item.dataKey === "Absents")?.value || 0;
  const sick =
    payload.find((item) => item.dataKey === "Malades/Blessés")?.value || 0;

  const total = present + absent + sick;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-xl dark:border-slate-700 dark:bg-slate-900">
      <p className="font-black text-slate-900 dark:text-slate-100">
        {formatShortDate(label)}
      </p>
      <div className="mt-2 space-y-1">
        <p className="text-slate-700 dark:text-slate-300">
          Présents : <span className="font-bold">{present}</span>
        </p>
        <p className="text-slate-700 dark:text-slate-300">
          Absents : <span className="font-bold">{absent}</span>
        </p>
        <p className="text-slate-700 dark:text-slate-300">
          Malades/Blessés : <span className="font-bold">{sick}</span>
        </p>
        <p className="border-t border-slate-200 pt-2 font-bold text-slate-900 dark:border-slate-700 dark:text-slate-100">
          Total : {total}
        </p>
      </div>
    </div>
  );
};

const PresenceBarChart = ({ data = [] }) => {
  const chartData = useMemo(() => {
    return (Array.isArray(data) ? data : [])
      .map((item) => {
        const presents = Number(item["Présents"] || 0);
        const absents = Number(item["Absents"] || 0);
        const sick = Number(item["Malades/Blessés"] || 0);

        return {
          ...item,
          Présents: presents,
          Absents: absents,
          "Malades/Blessés": sick,
          total: presents + absents + sick,
          shortDate: formatTinyDate(item.date),
        };
      })
      .filter((item) => item.total > 0);
  }, [data]);

  if (!chartData.length) {
    return (
      <div className="flex h-[300px] items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-800/60 dark:text-slate-400">
        Aucune donnée à afficher.
      </div>
    );
  }

  return (
    <div className="w-full font-satoshi">
      <div className="mb-4">
        <h3 className="text-lg font-black text-slate-900 dark:text-slate-100">
          Répartition par jour
        </h3>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Une colonne par jour avec le détail dans le survol.
        </p>
      </div>

      <ResponsiveContainer width="100%" height={320}>
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 10, left: -15, bottom: 10 }}
          barCategoryGap="18%"
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="shortDate"
            tick={{ fontSize: 11 }}
            minTickGap={18}
          />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            dataKey="Présents"
            stackId="day"
            fill="#34D399"
            radius={[4, 4, 0, 0]}
            maxBarSize={28}
          />
          <Bar
            dataKey="Absents"
            stackId="day"
            fill="#EF4444"
            maxBarSize={28}
          />
          <Bar
            dataKey="Malades/Blessés"
            stackId="day"
            fill="#FBBF24"
            radius={[4, 4, 0, 0]}
            maxBarSize={28}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PresenceBarChart;